import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import '../App.css';
import SearchableSelect from './SearchableSelect';
import { formatDisplayWeekdayDate } from '../utils/displayDate';

const OTHER = '__other__';
const TASK_OPTIONS = ['QC', 'Pull', 'Pack'];
const RESHELVE_WINDOW_DAYS = 5;
const SEP = '━━━━━━━━━━━━━━━━━━';
const FILLOUT_FORM_URL = 'https://mayker.fillout.com/t/jmSwEk1tEbus';

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const pad2 = (n) => String(n).padStart(2, '0');

// Date -> "YYYY-MM-DDTHH:mm" for <input type="datetime-local">.
function toLocalInput(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// Default schedule target: tomorrow at 6:00 AM local.
function defaultScheduleLocal() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(6, 0, 0, 0);
  return toLocalInput(d);
}

// Epoch ms -> "Tue, Jun 9, 6:00 AM".
function fmtWhen(ms) {
  return new Date(ms).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

// Canonical, serializable shape of an in-progress plan (a "draft").
function draftShape(s) {
  return {
    teamMessage: s.teamMessage || '',
    deliveries: s.deliveries || [],
    loadouts: s.loadouts || [],
    prep: s.prep || [],
    reshelve: s.reshelve || [],
    sideQuests: s.sideQuests || [],
    footer: s.footer || '',
    messageText: s.messageText || '',
    manuallyEdited: !!s.manuallyEdited
  };
}

// Friendly label (e.g. "Tue, Jun 9, 2026") from an ISO date.
function labelFromISO(iso) {
  const d = parseISO(iso);
  return d
    ? formatDisplayWeekdayDate(iso, { withYear: true })
    : iso;
}

function parseISO(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

function withinPastDays(dISO, baseISO, n) {
  const d = parseISO(dISO);
  const t = parseISO(baseISO);
  if (!d || !t) return false;
  const diff = Math.round((t - d) / 86400000);
  return diff >= 0 && diff <= n;
}

const has = (v) => !!(v && String(v).trim());

// Short, friendly date from an ISO (yyyy-MM-dd) string, e.g. "Jun 10".
function fmtShortISO(iso) {
  const d = parseISO(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Project date(s) to show in a dropdown: prefer the sheet's text range,
// otherwise fall back to the formatted sort date.
function optionDates(r) {
  if (has(r.projectDates)) return r.projectDates.trim();
  if (has(r.projectDate)) return r.projectDate.trim();
  return fmtShortISO(r.date);
}

// Name · Project Date(s) · RW Ticket
function optionLabel(r) {
  return [r.project, optionDates(r), r.rwTicket ? `RW ${r.rwTicket}` : '']
    .filter(Boolean).join('  ·  ');
}

// EPS (and anything that isn't Staging) sorts before SMPS/Staging.
function srcRank(source) {
  return source === 'Staging' ? 1 : 0;
}

// Sort option entries: EPS listings first, then SMPS, and chronologically by
// ISO date within each group. Undated entries sink to the bottom of their group.
// ascending = soonest first (default); false = most recent first.
function sortByDate(items, ascending = true) {
  return items.slice().sort((a, b) => {
    const ra = a.srcRank || 0;
    const rb = b.srcRank || 0;
    if (ra !== rb) return ra - rb;
    if (!a.sort && !b.sort) return 0;
    if (!a.sort) return 1;
    if (!b.sort) return -1;
    if (a.sort === b.sort) return 0;
    const cmp = a.sort < b.sort ? -1 : 1;
    return ascending ? cmp : -cmp;
  });
}

// Fields the Warehouse Manager is responsible for entering on an operation.
// Completion status is measured against these only — reference data (RW ticket,
// imported timing, etc.) does not count.
const REQ_FIELDS = [
  { key: 'lead', label: 'Team Lead' },
  { key: 'teamMembers', label: 'Crew', short: 'crew' },
  { key: 'vehicle', label: 'Assigned Vehicle', short: 'vehicle' },
  { key: 'departure', label: 'Departure Location', short: 'departure' }
];

function reqStatus(r) {
  const missing = REQ_FIELDS.filter((f) => !has(r[f.key]));
  return { done: REQ_FIELDS.length - missing.length, total: REQ_FIELDS.length, complete: missing.length === 0, missing };
}

// One-line collapsed summary for an operation.
function opSummary(noun, r, timing) {
  const parts = [noun];
  if (has(timing)) parts.push(timing.trim());
  if (has(r.lead)) parts.push(r.lead.trim());
  parts.push(has(r.teamMembers) ? 'Crew assigned' : 'Crew missing');
  parts.push(has(r.vehicle) ? r.vehicle.trim() : 'Vehicle missing');
  return parts.join(' · ');
}

// Compact day label for the sticky bar, e.g. "Saturday, Aug 8".
function shortDayLabel(iso) {
  const d = parseISO(iso);
  return d ? d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : iso;
}

function emptyDelivery() {
  return {
    selectKey: '', manual: false, project: '', rwTicket: '', lead: '',
    teamMembers: '', vehicle: '', departure: '', deliveryTime: '',
    packet: '', folderId: '', notes: ''
  };
}
function emptyLoadout() {
  return {
    selectKey: '', manual: false, project: '', rwTicket: '', lead: '',
    teamMembers: '', vehicle: '', departure: '', strikeTime: '', notes: ''
  };
}
function emptyPrep() {
  return {
    selectKey: '', manual: false, project: '', rwTicket: '', projectDate: '',
    task: [], pullLead: '', packLead: '', packSignoff: '',
    teamMembers: '', notes: ''
  };
}
function emptyReshelve() {
  return {
    selectKey: '', manual: false, project: '', rwTicket: '', lead: '',
    teamMembers: '', notes: ''
  };
}
function emptySideQuest() {
  return {
    selectKey: '', manual: false, project: '', rwTicket: '', lead: '',
    teamMembers: '', vehicle: '', departure: '', onSiteTime: '', notes: ''
  };
}

function deliveryFromSrc(src, i) {
  return {
    ...emptyDelivery(), selectKey: String(i), project: src.project,
    rwTicket: src.rwTicket, lead: src.lead, deliveryTime: src.deliveryTime,
    departure: src.origin, folderId: src.folderId
  };
}
function loadoutFromSrc(src, i) {
  return {
    ...emptyLoadout(), selectKey: String(i), project: src.project,
    rwTicket: src.rwTicket, lead: src.lead, strikeTime: src.strikeTime
  };
}
function sideQuestFromSrc(src, i) {
  return { ...emptySideQuest(), selectKey: String(i), project: src.project };
}

function ProductionSchedule({ scheduleApiUrl = '' }) {
  const apiConfigured = !!scheduleApiUrl;

  const [sources, setSources] = useState({ deliveries: [], loadouts: [], prep: [], sideQuests: [], reshelve: [] });
  const [loadingSources, setLoadingSources] = useState(false);
  const [sourcesError, setSourcesError] = useState('');

  const [scheduleISO, setScheduleISO] = useState(todayISO());
  const date = useMemo(() => labelFromISO(scheduleISO), [scheduleISO]);
  const [teamMessage, setTeamMessage] = useState('');
  const [deliveries, setDeliveries] = useState([]);
  const [loadouts, setLoadouts] = useState([]);
  const [prep, setPrep] = useState([]);
  const [reshelve, setReshelve] = useState([]);
  const [sideQuests, setSideQuests] = useState([]);
  const [footer, setFooter] = useState('');

  const [messageText, setMessageText] = useState('');
  const [manuallyEdited, setManuallyEdited] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState('');

  const [scheduleAt, setScheduleAt] = useState(defaultScheduleLocal());
  const [scheduling, setScheduling] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState('');
  const [scheduledList, setScheduledList] = useState([]);

  const initializedRef = useRef(false);  // first load (draft or sheet) done
  const userEditedRef = useRef(false);    // a real user edit happened since last load/save
  const [draftChecked, setDraftChecked] = useState(false); // initial draft lookup complete
  const [draftInfo, setDraftInfo] = useState(null);        // { savedAt } when a draft is active
  const [autosaveState, setAutosaveState] = useState('');  // '' | 'saving' | 'saved' | 'error'

  // UI-only state (no effect on saved data / business logic).
  const [dateEditing, setDateEditing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set()); // keys of manually-collapsed events
  const eventRefs = useRef({});   // key -> event container element
  const reviewRef = useRef(null); // Review & Send section

  const markEdited = useCallback(() => { userEditedRef.current = true; }, []);

  const toggleCollapse = useCallback((key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);
  const expandKey = useCallback((key) => {
    setCollapsed((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const resolvePacket = useCallback(async (folderId) => {
    if (!apiConfigured || !folderId) return '';
    try {
      const resp = await fetch(`${scheduleApiUrl}?action=resolvePacket&folderId=${encodeURIComponent(folderId)}`);
      const data = await resp.json();
      return data.success ? (data.url || '') : '';
    } catch (_) {
      return '';
    }
  }, [scheduleApiUrl, apiConfigured]);

  const fetchSources = useCallback(async () => {
    if (!apiConfigured) return;
    setLoadingSources(true);
    setSourcesError('');
    try {
      const resp = await fetch(`${scheduleApiUrl}?action=getScheduleSources`);
      const data = await resp.json();
      if (data.success) {
        setSources({
          deliveries: data.deliveries || [], loadouts: data.loadouts || [],
          prep: data.prep || [], sideQuests: data.sideQuests || [],
          reshelve: data.reshelve || []
        });
        setLastSyncedAt(Date.now());
      } else {
        setSourcesError(data.error || 'Failed to load schedule sources');
      }
    } catch (err) {
      setSourcesError(err.message || 'Network error');
    } finally {
      setLoadingSources(false);
    }
  }, [scheduleApiUrl, apiConfigured]);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  // Populate the date-driven sections (deliveries, load-outs, field ops) for a
  // given ISO date. Replaces those sections so the plan matches the chosen day.
  const populateForDate = useCallback((iso) => {
    const pick = (arr, fromSrc) => arr.map((src, i) => ({ src, i }))
      .filter(({ src }) => src.date === iso)
      .map(({ src, i }) => fromSrc(src, i));

    const dRows = pick(sources.deliveries, deliveryFromSrc);
    setDeliveries(dRows);
    dRows.forEach((row, idx) => {
      if (row.folderId) {
        resolvePacket(row.folderId).then((url) => {
          if (url) setDeliveries((rows) => rows.map((r, i) => (i === idx ? { ...r, packet: url } : r)));
        });
      }
    });

    const lRows = pick(sources.loadouts, loadoutFromSrc);
    setLoadouts(lRows);

    const sqRows = pick(sources.sideQuests, sideQuestFromSrc);
    setSideQuests(sqRows);

    setDraftInfo(null);
    userEditedRef.current = false;
  }, [sources, resolvePacket]);

  // Hydrate all editable sections from a saved draft.
  const applyDraft = useCallback((draft) => {
    const d = draft.data || {};
    setTeamMessage(d.teamMessage || '');
    setDeliveries(d.deliveries || []);
    setLoadouts(d.loadouts || []);
    setPrep(d.prep || []);
    setReshelve(d.reshelve || []);
    setSideQuests(d.sideQuests || []);
    setFooter(d.footer || '');
    setManuallyEdited(!!d.manuallyEdited);
    if (typeof d.messageText === 'string') setMessageText(d.messageText);
    setDraftInfo({ savedAt: draft.savedAt });
    userEditedRef.current = false;
  }, []);

  // Fetch a saved draft for a date (or null).
  const fetchDraft = useCallback(async (iso) => {
    if (!apiConfigured) return null;
    try {
      const resp = await fetch(`${scheduleApiUrl}?action=getDraft&date=${encodeURIComponent(iso)}`);
      const data = await resp.json();
      return (data.success && data.draft && data.draft.data) ? data.draft : null;
    } catch (_) {
      return null;
    }
  }, [scheduleApiUrl, apiConfigured]);

  // Autosave the in-progress plan. Quiet by design — surfaces state through the
  // subtle autosave indicator in the header rather than a prominent control.
  const saveDraft = useCallback(async (iso, data) => {
    if (!apiConfigured) return;
    try {
      const resp = await fetch(scheduleApiUrl, {
        method: 'POST', headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ action: 'saveDraft', date: iso, data }), mode: 'cors'
      });
      const d = await resp.json();
      if (d.success) {
        userEditedRef.current = false;
        setDraftInfo({ savedAt: d.savedAt || Date.now() });
        setAutosaveState('saved');
      } else {
        setAutosaveState('error');
      }
    } catch (err) {
      setAutosaveState('error');
    }
  }, [scheduleApiUrl, apiConfigured]);

  // Initial draft lookup for the starting date (independent of sheet sources).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const draft = await fetchDraft(scheduleISO);
      if (cancelled) return;
      if (draft) { initializedRef.current = true; applyDraft(draft); }
      setDraftChecked(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If there's no draft, populate from the sheets once sources are available.
  useEffect(() => {
    if (initializedRef.current || !draftChecked) return;
    const hasData = sources.deliveries.length || sources.loadouts.length || sources.sideQuests.length;
    if (!hasData) return;
    initializedRef.current = true;
    populateForDate(scheduleISO);
  }, [sources, populateForDate, scheduleISO, draftChecked]);

  // Changing the date loads that date's saved draft, or auto-pulls from sheets.
  const onChangeDate = async (iso) => {
    if (!iso) return;
    setScheduleISO(iso);
    userEditedRef.current = false;
    const draft = await fetchDraft(iso);
    if (draft) applyDraft(draft);
    else populateForDate(iso);
  };

  // Auto-save the in-progress plan (debounced) after a real user edit.
  useEffect(() => {
    if (!apiConfigured || !userEditedRef.current) return;
    setAutosaveState('saving');
    const data = draftShape({ teamMessage, deliveries, loadouts, prep, reshelve, sideQuests, footer, messageText, manuallyEdited });
    const t = setTimeout(() => { saveDraft(scheduleISO, data); }, 1500);
    return () => clearTimeout(t);
  }, [teamMessage, deliveries, loadouts, prep, reshelve, sideQuests, footer, messageText, manuallyEdited, scheduleISO, apiConfigured, saveDraft]);

  // Reset-to-sheets (destructive) — tucked in the header overflow menu.
  const resetToSheets = async () => {
    if (!apiConfigured) return;
    if (!window.confirm('Reset to sheet data? This discards your changes for this date and re-pulls from EPS & SMPs.')) return;
    try {
      await fetch(scheduleApiUrl, {
        method: 'POST', headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ action: 'deleteDraft', date: scheduleISO }), mode: 'cors'
      });
    } catch (_) { /* non-fatal */ }
    setAutosaveState('');
    populateForDate(scheduleISO);
  };

  // Build dropdown options: "Name · Project Date(s) · RW Ticket", sorted
  // chronologically (soonest first) so the most likely picks are at the top.
  const buildOptions = (rows) => {
    const items = rows.map((r, i) => ({ value: String(i), sort: r.date || '', srcRank: srcRank(r.source), label: optionLabel(r) }));
    const opts = sortByDate(items, true).map(({ value, label }) => ({ value, label }));
    opts.push({ value: OTHER, label: 'Other — enter manually' });
    return opts;
  };

  const deliveryOptions = useMemo(() => buildOptions(sources.deliveries), [sources.deliveries]);
  const loadoutOptions = useMemo(() => buildOptions(sources.loadouts), [sources.loadouts]);
  const prepOptions = useMemo(() => buildOptions(sources.prep), [sources.prep]);
  const sideQuestOptions = useMemo(() => buildOptions(sources.sideQuests), [sources.sideQuests]);

  // Reshelve: recent strikes from EPS + SMPS (active and completed tabs),
  // trimmed to the last RESHELVE_WINDOW_DAYS days, most recent strike first.
  const reshelveOptions = useMemo(() => {
    const items = sources.reshelve.map((r, i) => ({ r, i }))
      .filter(({ r }) => withinPastDays(r.date, scheduleISO, RESHELVE_WINDOW_DAYS))
      .map(({ r, i }) => ({ value: String(i), sort: r.date || '', srcRank: srcRank(r.source), label: optionLabel(r) }));
    const opts = sortByDate(items, false).map(({ value, label }) => ({ value, label }));
    opts.push({ value: OTHER, label: 'Other — enter manually' });
    return opts;
  }, [sources.reshelve, scheduleISO]);

  // ── Row helpers ───────────────────────────────────────
  const updateRow = (setter, idx, patch) => {
    markEdited();
    setter((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeRow = (setter, idx) => {
    markEdited();
    setter((rows) => rows.filter((_, i) => i !== idx));
  };
  const addRow = (setter, factory) => {
    markEdited();
    setter((rows) => [...rows, factory()]);
  };

  const onSelectDelivery = (idx, value) => {
    if (value === OTHER) { updateRow(setDeliveries, idx, { ...emptyDelivery(), selectKey: OTHER, manual: true }); return; }
    const src = sources.deliveries[Number(value)];
    if (!src) return;
    updateRow(setDeliveries, idx, { ...deliveryFromSrc(src, Number(value)) });
    if (src.folderId) resolvePacket(src.folderId).then((url) => { if (url) updateRow(setDeliveries, idx, { packet: url }); });
  };
  const onSelectLoadout = (idx, value) => {
    if (value === OTHER) { updateRow(setLoadouts, idx, { ...emptyLoadout(), selectKey: OTHER, manual: true }); return; }
    const src = sources.loadouts[Number(value)];
    if (src) updateRow(setLoadouts, idx, { ...loadoutFromSrc(src, Number(value)) });
  };
  const onSelectPrep = (idx, value) => {
    if (value === OTHER) { updateRow(setPrep, idx, { ...emptyPrep(), selectKey: OTHER, manual: true }); return; }
    const src = sources.prep[Number(value)];
    if (src) updateRow(setPrep, idx, {
      selectKey: value, manual: false, project: src.project, rwTicket: src.rwTicket, projectDate: src.projectDate
    });
  };
  const onSelectReshelve = (idx, value) => {
    if (value === OTHER) { updateRow(setReshelve, idx, { ...emptyReshelve(), selectKey: OTHER, manual: true }); return; }
    const src = sources.reshelve[Number(value)];
    if (src) updateRow(setReshelve, idx, {
      selectKey: value, manual: false, project: src.project, rwTicket: src.rwTicket, lead: src.lead
    });
  };
  const onSelectSideQuest = (idx, value) => {
    if (value === OTHER) { updateRow(setSideQuests, idx, { ...emptySideQuest(), selectKey: OTHER, manual: true }); return; }
    const src = sources.sideQuests[Number(value)];
    if (src) updateRow(setSideQuests, idx, { ...sideQuestFromSrc(src, Number(value)) });
  };

  const toggleTask = (idx, t) => {
    markEdited();
    setPrep((rows) => rows.map((r, i) => {
      if (i !== idx) return r;
      const hasT = r.task.includes(t);
      return { ...r, task: hasT ? r.task.filter((x) => x !== t) : [...r.task, t] };
    }));
  };

  // ── Operation completion (Warehouse Manager inputs only) ──
  // Progress reflects only the fields the Warehouse Manager is responsible for:
  // Team Lead, Crew, Assigned Vehicle, Departure Location. Timing/RW/etc. are
  // reference information and are not counted toward completion.
  const operations = useMemo(() => {
    const rows = [];
    const add = (kind, noun, list, timingKey) => list.forEach((r, idx) => {
      if (!has(r.project)) return;
      rows.push({ kind, noun, idx, r, key: `${kind}-${idx}`, timing: r[timingKey], ...reqStatus(r) });
    });
    add('delivery', 'Delivery', deliveries, 'deliveryTime');
    add('loadout', 'Load-Out', loadouts, 'strikeTime');
    add('field', 'Field Op', sideQuests, 'onSiteTime');
    return rows;
  }, [deliveries, loadouts, sideQuests]);

  const progress = useMemo(() => {
    const total = operations.length;
    const ready = operations.filter((o) => o.complete).length;
    const missingBy = { lead: 0, teamMembers: 0, vehicle: 0, departure: 0 };
    operations.forEach((o) => o.missing.forEach((f) => { missingBy[f.key] += 1; }));
    const incomplete = operations.filter((o) => !o.complete);
    const detailsNeeded = operations.reduce((sum, o) => sum + o.missing.length, 0);
    const parts = [];
    if (missingBy.lead) parts.push(`${missingBy.lead} missing lead`);
    if (missingBy.teamMembers) parts.push(`${missingBy.teamMembers} missing crew`);
    if (missingBy.vehicle) parts.push(`${missingBy.vehicle} missing vehicle`);
    if (missingBy.departure) parts.push(`${missingBy.departure} missing departure`);
    return { total, ready, incomplete: incomplete.length, detailsNeeded, summary: parts.join(' · '), first: incomplete[0] || null };
  }, [operations]);

  const jumpToFirstIncomplete = useCallback(() => {
    const first = progress.first;
    if (!first) return;
    expandKey(first.key);
    setTimeout(() => {
      const el = eventRefs.current[first.key];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const input = el.querySelector('.dpp-missing input, .dpp-missing textarea');
        if (input) setTimeout(() => input.focus({ preventScroll: true }), 350);
      }
    }, 40);
  }, [progress.first, expandKey]);

  // ── Message composition ───────────────────────────────
  const composeMessage = useCallback(() => {
    const L = [];
    L.push(`*PRODUCTION SCHEDULE — ${date}*`);
    if (teamMessage.trim()) { L.push(''); L.push(`_${teamMessage.trim()}_`); }
    L.push('');
    L.push(
      "Please see today's production schedule. For all active projects and field operations, " +
      `please use the <${FILLOUT_FORM_URL}|fill-out form>.`
    );
    L.push('', SEP, '*ACTIVE PROJECTS*', SEP, '');

    // DELIVERIES
    L.push('🚚 *DELIVERIES*');
    const dels = deliveries.filter((r) => r.project.trim());
    if (!dels.length) L.push('• _No deliveries scheduled._');
    dels.forEach((r, i) => {
      if (i) L.push(''); // small break between projects
      L.push(`• *${r.project}*${r.rwTicket ? `  (RW #${r.rwTicket})` : ''}`);
      L.push(`   Lead: ${r.lead || '—'}  |  Crew: ${r.teamMembers || '—'}`);
      if (r.vehicle) L.push(`   Vehicle: ${r.vehicle}`);
      if (r.departure) L.push(`   Departure: ${r.departure}`);
      if (r.deliveryTime) L.push(`   Arrival: ${r.deliveryTime}`);
      if (r.packet) L.push(`   <${r.packet}|Production Packet>`);
      if (r.notes) L.push(`   📝 ${r.notes}`);
    });

    L.push('', ''); // paragraph break between subsections

    // LOAD-OUTS
    L.push('🚛 *LOAD-OUTS*');
    const los = loadouts.filter((r) => r.project.trim());
    if (!los.length) L.push('• _No load-outs scheduled._');
    los.forEach((r, i) => {
      if (i) L.push('');
      L.push(`• *${r.project}*${r.rwTicket ? `  (RW #${r.rwTicket})` : ''}`);
      L.push(`   Lead: ${r.lead || '—'}  |  Crew: ${r.teamMembers || '—'}`);
      if (r.vehicle) L.push(`   Vehicle: ${r.vehicle}`);
      if (r.departure) L.push(`   Departure: ${r.departure}`);
      if (r.strikeTime) L.push(`   Strike: ${r.strikeTime}`);
      if (r.notes) L.push(`   📝 ${r.notes}`);
    });

    L.push('', ''); // paragraph break between subsections

    // PICKUPS, RETURNS & FIELD OPERATIONS
    L.push('📍 *PICKUPS, RETURNS & FIELD OPERATIONS*');
    const sq = sideQuests.filter((r) => r.project.trim());
    if (!sq.length) L.push('• _No field operations scheduled._');
    sq.forEach((r, i) => {
      if (i) L.push('');
      L.push(`• *${r.project}*${r.rwTicket ? `  (RW #${r.rwTicket})` : ''}`);
      L.push(`   Lead: ${r.lead || '—'}  |  Crew: ${r.teamMembers || '—'}`);
      if (r.vehicle) L.push(`   Vehicle: ${r.vehicle}`);
      if (r.departure) L.push(`   Departure: ${r.departure}`);
      if (r.onSiteTime) L.push(`   Arrival: ${r.onSiteTime}`);
      if (r.notes) L.push(`   📝 ${r.notes}`);
    });

    L.push('', SEP, '📦 *QC, PULL, & PACK*', SEP);
    const pr = prep.filter((r) => r.project.trim());
    if (!pr.length) L.push('• _No QC, pull, or pack work scheduled._');
    pr.forEach((r, i) => {
      if (i) L.push('');
      const meta = [r.rwTicket ? `RW #${r.rwTicket}` : '', r.projectDate].filter(Boolean).join(', ');
      L.push(`• *${r.project}*${meta ? `  (${meta})` : ''}`);
      L.push(`   Task: ${r.task.length ? r.task.join(', ') : '—'}`);
      const leads = [
        r.pullLead ? `Pull Lead: ${r.pullLead}` : '',
        r.packLead ? `Pack Lead: ${r.packLead}` : '',
        r.packSignoff ? `Sign-Off: ${r.packSignoff}` : ''
      ].filter(Boolean).join('  |  ');
      if (leads) L.push(`   ${leads}`);
      if (r.teamMembers) L.push(`   Crew: ${r.teamMembers}`);
      if (r.notes) L.push(`   📝 ${r.notes}`);
    });

    L.push('', SEP, '🔄 *RETURNS PROCESSING*', SEP);
    const rs = reshelve.filter((r) => r.project.trim());
    if (!rs.length) L.push('• _No returns processing scheduled._');
    rs.forEach((r, i) => {
      if (i) L.push('');
      L.push(`• *${r.project}*${r.rwTicket ? `  (RW #${r.rwTicket})` : ''}`);
      L.push(`   Lead: ${r.lead || '—'}  |  Crew: ${r.teamMembers || '—'}`);
      if (r.notes) L.push(`   📝 ${r.notes}`);
    });

    if (footer.trim()) L.push('', SEP, `ℹ️ *Heads up:* ${footer.trim()}`);
    return L.join('\n');
  }, [date, teamMessage, deliveries, loadouts, prep, reshelve, sideQuests, footer]);

  useEffect(() => {
    if (!manuallyEdited) setMessageText(composeMessage());
  }, [composeMessage, manuallyEdited]);

  const regenerate = () => { setMessageText(composeMessage()); setManuallyEdited(false); };

  const handleSend = async () => {
    if (!apiConfigured || !messageText.trim()) return;
    setSending(true);
    setSendStatus('');
    try {
      const resp = await fetch(scheduleApiUrl, {
        method: 'POST', headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ action: 'postSchedule', text: messageText }), mode: 'cors'
      });
      const data = await resp.json();
      setSendStatus(data.success ? 'Posted to Slack ✓' : `Failed: ${data.error || 'Unknown error'}`);
    } catch (err) {
      setSendStatus(`Failed: ${err.message || 'Network error'}`);
    } finally {
      setSending(false);
    }
  };

  // Publish immediately. Existing business rules allow incomplete schedules to
  // be sent, so we confirm rather than block when work is still outstanding.
  const publishSchedule = () => {
    if (!apiConfigured || !messageText.trim()) return;
    if (progress.incomplete > 0) {
      const ok = window.confirm(
        `This schedule contains ${progress.incomplete} incomplete operation${progress.incomplete === 1 ? '' : 's'}. Publish anyway?`
      );
      if (!ok) return;
    }
    handleSend();
  };

  const fetchScheduled = useCallback(async () => {
    if (!apiConfigured) return;
    try {
      const resp = await fetch(`${scheduleApiUrl}?action=listScheduled`);
      const data = await resp.json();
      if (data.success) setScheduledList(data.items || []);
    } catch (_) { /* non-fatal */ }
  }, [scheduleApiUrl, apiConfigured]);

  useEffect(() => { fetchScheduled(); }, [fetchScheduled]);

  const handleSchedule = async () => {
    if (!apiConfigured || !messageText.trim()) return;
    const when = scheduleAt ? new Date(scheduleAt).getTime() : NaN;
    if (!when || isNaN(when) || when <= Date.now()) {
      setScheduleStatus('Pick a time in the future.');
      return;
    }
    setScheduling(true);
    setScheduleStatus('');
    try {
      const resp = await fetch(scheduleApiUrl, {
        method: 'POST', headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ action: 'schedulePost', text: messageText, sendAt: when }), mode: 'cors'
      });
      const data = await resp.json();
      if (data.success) {
        setScheduleStatus(`Scheduled for ${fmtWhen(when)} ✓`);
        fetchScheduled();
      } else {
        setScheduleStatus(`Failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      setScheduleStatus(`Failed: ${err.message || 'Network error'}`);
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelScheduled = async (id) => {
    if (!apiConfigured) return;
    try {
      await fetch(scheduleApiUrl, {
        method: 'POST', headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ action: 'cancelScheduled', id }), mode: 'cors'
      });
      fetchScheduled();
    } catch (_) { /* non-fatal */ }
  };

  if (!apiConfigured) {
    return (
      <div className="form-section">
        <p style={{ color: 'var(--brand-text-light)' }}>
          Production Schedule requires REACT_APP_SCHEDULE_API_URL to be configured.
        </p>
      </div>
    );
  }

  return (
    <div className="dpp">
      {/* DATE / SYNC / AUTOSAVE */}
      <div className="dpp-daterow">
        <div className="dpp-datebox">
          {dateEditing ? (
            <input type="date" className="input-field dpp-date-input" value={scheduleISO} autoFocus
              onChange={(e) => onChangeDate(e.target.value)} onBlur={() => setDateEditing(false)} />
          ) : (
            <>
              <span className="dpp-date-label">{date}</span>
              <button type="button" className="dpp-link" onClick={() => setDateEditing(true)}>Change Date</button>
            </>
          )}
        </div>
        <div className="dpp-headmeta">
          <span className="dpp-syncbox">
            <Icon name="refresh" />
            <span>Synced from EPS &amp; SMPs{lastSyncedAt ? ` · ${fmtWhen(lastSyncedAt)}` : ''}</span>
            <button type="button" className="dpp-link" onClick={fetchSources} disabled={loadingSources}>
              {loadingSources ? 'Refreshing…' : 'Refresh'}
            </button>
          </span>
          <details className="dpp-menu">
            <summary className="dpp-menu-btn" aria-label="More options"><Icon name="dots" /></summary>
            <div className="dpp-menu-list">
              <button type="button" className="dpp-menu-item" onClick={resetToSheets}>Reset to Sheet Data</button>
            </div>
          </details>
        </div>
      </div>
      <div className="dpp-subrow">
        <span className="helper-text dpp-subtle">Operations are automatically pulled from EPS and SMPs for this date.</span>
        {(() => {
          const label = autosaveState === 'saving' ? 'Saving…'
            : autosaveState === 'error' ? 'Save failed — retrying'
              : (autosaveState === 'saved' || draftInfo) ? '✓ Saved' : '';
          return label ? <span className="dpp-autosave">{label}</span> : null;
        })()}
      </div>

      {sourcesError && <div className="dpp-alert">{sourcesError}</div>}
      {sources.deliveries.length === 0 && !loadingSources && !sourcesError && (
        <div className="dpp-alert">No project data loaded — confirm the schedule script is redeployed.</div>
      )}

      {/* TEAM MESSAGE — top of the Slack post */}
      <div className="dpp-block">
        <div className="dpp-section-label">Team Message</div>
        <span className="helper-text dpp-subtle">Shown at the top of the Slack post.</span>
        <input type="text" className="input-field dpp-teammsg" value={teamMessage}
          onChange={(e) => { markEdited(); setTeamMessage(e.target.value); }}
          placeholder="Add a note for the crew…" />
      </div>

      {/* TODAY'S OPERATIONS */}
      <div className="dpp-section-label dpp-ops-label">Today's Operations</div>

      {/* DELIVERIES */}
      <CategoryHeader icon="truck" title="Deliveries" count={deliveries.length} />
      {deliveries.length === 0
        ? <EmptyCategory label="No deliveries scheduled." addLabel="+ Add Delivery" onAdd={() => addRow(setDeliveries, emptyDelivery)} />
        : (
          <>
            {deliveries.map((r, idx) => (
              <OperationEvent key={idx} kind="delivery" noun="Delivery" r={r} idx={idx}
                options={deliveryOptions} timingKey="deliveryTime" timingLabel="Arrival Time" showPacket
                onSelect={onSelectDelivery} onUpdate={(i, patch) => updateRow(setDeliveries, i, patch)}
                onRemove={(i) => removeRow(setDeliveries, i)} collapsed={collapsed} onToggleCollapse={toggleCollapse}
                loading={loadingSources} refFor={(k, el) => { eventRefs.current[k] = el; }} />
            ))}
            <AddLink label="+ Add Delivery" onClick={() => addRow(setDeliveries, emptyDelivery)} />
          </>
        )}

      {/* LOAD-OUTS */}
      <CategoryHeader icon="loadout" title="Load-Outs" count={loadouts.length} />
      {loadouts.length === 0
        ? <EmptyCategory label="No load-outs scheduled." addLabel="+ Add Load-Out" onAdd={() => addRow(setLoadouts, emptyLoadout)} />
        : (
          <>
            {loadouts.map((r, idx) => (
              <OperationEvent key={idx} kind="loadout" noun="Load-Out" r={r} idx={idx}
                options={loadoutOptions} timingKey="strikeTime" timingLabel="Strike Time"
                onSelect={onSelectLoadout} onUpdate={(i, patch) => updateRow(setLoadouts, i, patch)}
                onRemove={(i) => removeRow(setLoadouts, i)} collapsed={collapsed} onToggleCollapse={toggleCollapse}
                loading={loadingSources} refFor={(k, el) => { eventRefs.current[k] = el; }} />
            ))}
            <AddLink label="+ Add Load-Out" onClick={() => addRow(setLoadouts, emptyLoadout)} />
          </>
        )}

      {/* PICKUPS, RETURNS & FIELD OPERATIONS */}
      <CategoryHeader icon="pin" title="Pickups, Returns & Field Operations" count={sideQuests.length} />
      {sideQuests.length === 0
        ? <EmptyCategory label="No field operations scheduled." addLabel="+ Add Field Operation" onAdd={() => addRow(setSideQuests, emptySideQuest)} />
        : (
          <>
            {sideQuests.map((r, idx) => (
              <OperationEvent key={idx} kind="field" noun="Field Op" r={r} idx={idx}
                options={sideQuestOptions} timingKey="onSiteTime" timingLabel="Arrival Time" projectLabel="Activity / Project Name"
                onSelect={onSelectSideQuest} onUpdate={(i, patch) => updateRow(setSideQuests, i, patch)}
                onRemove={(i) => removeRow(setSideQuests, i)} collapsed={collapsed} onToggleCollapse={toggleCollapse}
                loading={loadingSources} refFor={(k, el) => { eventRefs.current[k] = el; }} />
            ))}
            <AddLink label="+ Add Field Operation" onClick={() => addRow(setSideQuests, emptySideQuest)} />
          </>
        )}

      {/* QC, PULL, & PACK */}
      <CategoryHeader icon="box" title="QC, Pull & Pack" count={prep.length} />
      {prep.length === 0
        ? <EmptyCategory label="No QC, pull, or pack work scheduled." addLabel="+ Add Prep Item" onAdd={() => addRow(setPrep, emptyPrep)} />
        : (
          <>
            {prep.map((r, idx) => (
              <PlainEvent key={idx} kind="prep" r={r} idx={idx} noun="QC / Pull / Pack" summary={prepSummary(r)}
                collapsed={collapsed} onToggleCollapse={toggleCollapse} refFor={(k, el) => { eventRefs.current[k] = el; }}
                onRemove={() => removeRow(setPrep, idx)}>
                <SubLabel>Project</SubLabel>
                <Picker options={prepOptions} value={r.selectKey} onChange={(v) => onSelectPrep(idx, v)} loading={loadingSources} />
                {(r.selectKey || r.manual) && (
                  <>
                    {r.manual && <EField label="Project Name" value={r.project} onChange={(v) => updateRow(setPrep, idx, { project: v })} />}
                    <SubLabel>Task</SubLabel>
                    <div className="dpp-tasks">
                      {TASK_OPTIONS.map((t) => {
                        const checked = r.task.includes(t);
                        return (
                          <button type="button" key={t} onClick={() => toggleTask(idx, t)}
                            className={`dpp-task${checked ? ' dpp-task-on' : ''}`}>{checked ? '✓ ' : ''}{t}</button>
                        );
                      })}
                    </div>
                    <SubLabel>Assignment</SubLabel>
                    <div className="form-row">
                      <EField half label="Pull Lead" value={r.pullLead} onChange={(v) => updateRow(setPrep, idx, { pullLead: v })} />
                      <EField half label="Pack Lead" value={r.packLead} onChange={(v) => updateRow(setPrep, idx, { packLead: v })} />
                    </div>
                    <div className="form-row">
                      <EField half label="Pack Sign-Off" value={r.packSignoff} onChange={(v) => updateRow(setPrep, idx, { packSignoff: v })} />
                      <EField half label="Crew" placeholder="e.g. Tye, Dre, Jalan" value={r.teamMembers} onChange={(v) => updateRow(setPrep, idx, { teamMembers: v })} />
                    </div>
                    {r.manual ? (
                      <>
                        <SubLabel>Reference</SubLabel>
                        <div className="form-row">
                          <EField half label="RW Ticket" optional value={r.rwTicket} onChange={(v) => updateRow(setPrep, idx, { rwTicket: v })} />
                          <EField half label="Project Date" optional value={r.projectDate} onChange={(v) => updateRow(setPrep, idx, { projectDate: v })} />
                        </div>
                      </>
                    ) : (has(r.rwTicket) || has(r.projectDate)) && (
                      <>
                        <SubLabel>Reference</SubLabel>
                        <div className="dpp-refrow">
                          {has(r.rwTicket) && <RefField label="RW Ticket" value={r.rwTicket} />}
                          {has(r.projectDate) && <RefField label="Project Date" value={r.projectDate} />}
                        </div>
                      </>
                    )}
                    <SubLabel>Notes</SubLabel>
                    <EField labelHidden label="Notes" optional placeholder="Add any special instructions for this operation…" value={r.notes} onChange={(v) => updateRow(setPrep, idx, { notes: v })} />
                  </>
                )}
              </PlainEvent>
            ))}
            <AddLink label="+ Add Prep Item" onClick={() => addRow(setPrep, emptyPrep)} />
          </>
        )}

      {/* RETURNS PROCESSING */}
      <CategoryHeader icon="returns" title="Returns Processing" count={reshelve.length} />
      {reshelve.length === 0
        ? <EmptyCategory label={`No returns processing scheduled.`} addLabel="+ Add Returns Item" onAdd={() => addRow(setReshelve, emptyReshelve)} />
        : (
          <>
            {reshelve.map((r, idx) => (
              <PlainEvent key={idx} kind="reshelve" r={r} idx={idx} noun="Returns" summary={reshelveSummary(r)}
                collapsed={collapsed} onToggleCollapse={toggleCollapse} refFor={(k, el) => { eventRefs.current[k] = el; }}
                onRemove={() => removeRow(setReshelve, idx)}>
                <SubLabel>Project</SubLabel>
                <Picker options={reshelveOptions} value={r.selectKey} onChange={(v) => onSelectReshelve(idx, v)} loading={loadingSources} />
                {(r.selectKey || r.manual) && (
                  <>
                    {r.manual && <EField label="Project Name" value={r.project} onChange={(v) => updateRow(setReshelve, idx, { project: v })} />}
                    <SubLabel>Assignment</SubLabel>
                    <div className="form-row">
                      <EField half label="Team Lead" value={r.lead} onChange={(v) => updateRow(setReshelve, idx, { lead: v })} />
                      <EField half label="Crew" placeholder="e.g. Tye, Dre, Jalan" value={r.teamMembers} onChange={(v) => updateRow(setReshelve, idx, { teamMembers: v })} />
                    </div>
                    {r.manual ? (
                      <>
                        <SubLabel>Reference</SubLabel>
                        <div className="form-row">
                          <EField half label="RW Ticket" optional value={r.rwTicket} onChange={(v) => updateRow(setReshelve, idx, { rwTicket: v })} />
                        </div>
                      </>
                    ) : has(r.rwTicket) && (
                      <>
                        <SubLabel>Reference</SubLabel>
                        <div className="dpp-refrow"><RefField label="RW Ticket" value={r.rwTicket} /></div>
                      </>
                    )}
                    <SubLabel>Notes</SubLabel>
                    <EField labelHidden label="Notes" optional placeholder="Add any special instructions for this operation…" value={r.notes} onChange={(v) => updateRow(setReshelve, idx, { notes: v })} />
                  </>
                )}
              </PlainEvent>
            ))}
            <AddLink label="+ Add Returns Item" onClick={() => addRow(setReshelve, emptyReshelve)} />
          </>
        )}

      {/* DAILY OPERATIONS NOTE — bottom of the Slack post */}
      <div className="dpp-block dpp-note-block">
        <div className="dpp-section-label">Daily Operations Note</div>
        <span className="helper-text dpp-subtle">Shown at the bottom of the Slack post.</span>
        <AutoGrowTextarea className="input-field dpp-note" value={footer} minHeight={64}
          onChange={(e) => { markEdited(); setFooter(e.target.value); }}
          placeholder="Add a note that applies to the entire production day…" />
      </div>

      {/* REVIEW & SEND */}
      <div ref={reviewRef} className="dpp-review">
        <div className="dpp-section-label dpp-review-title">Review &amp; Send</div>

        {/* SLACK PREVIEW */}
        <div className="dpp-sub-label dpp-sub-icon">
          <Icon name="chat" /><span>Slack Preview</span>
        </div>
        <span className="helper-text dpp-subtle">This is what the team will receive.</span>
        <div className="dpp-slack">
          <div className="dpp-slack-head">
            <span className="dpp-slack-avatar">M</span>
            <span className="dpp-slack-name">Mayker Administrative</span>
            <span className="dpp-slack-tag">APP</span>
            <span className="dpp-slack-preview">preview</span>
          </div>
          <textarea className="input-field dpp-slack-text" rows={18}
            value={messageText} onChange={(e) => { markEdited(); setMessageText(e.target.value); setManuallyEdited(true); }} />
        </div>
        <div className="dpp-slack-actions">
          <button type="button" className="dpp-link" onClick={regenerate}>Refresh Preview</button>
          {!manuallyEdited && (
            <span className="helper-text dpp-subtle" style={{ margin: 0 }}>Preview updates automatically as you edit the schedule.</span>
          )}
        </div>

        {/* FINAL STATUS + ACTION — the one page-level readiness indicator */}
        <div className="dpp-final">
          <span className="dpp-final-status">
            <span className="dpp-final-date">{shortDayLabel(scheduleISO)}</span>
            <span className="dpp-final-sep">·</span>
            <span>{progress.total} operation{progress.total === 1 ? '' : 's'}</span>
            {progress.total > 0 && (
              <>
                <span className="dpp-final-sep">·</span>
                {progress.incomplete === 0 ? (
                  <span className="dpp-status dpp-status-ok"><Icon name="check" />Ready to send</span>
                ) : (
                  <span className="dpp-status dpp-status-warn">
                    <Icon name="alert" />{progress.detailsNeeded} detail{progress.detailsNeeded === 1 ? '' : 's'} still needed
                  </span>
                )}
              </>
            )}
          </span>
          <div className="dpp-final-actions">
            {progress.incomplete > 0 && (
              <button type="button" className="dpp-link" onClick={jumpToFirstIncomplete}>Review Missing Details</button>
            )}
            <button type="button" className="btn-calculate dpp-send" onClick={publishSchedule} disabled={sending || !messageText.trim()}>
              {sending ? 'Sending…' : 'Send Daily Plan →'}
            </button>
          </div>
        </div>
        {sendStatus && <div className="helper-text" style={{ marginTop: '10px' }}>{sendStatus}</div>}

        {/* SCHEDULE FOR LATER — secondary */}
        <div className="dpp-schedule-later">
          <div className="dpp-sub-label dpp-schedule-icon"><Icon name="clock" /><span>Schedule for later</span></div>
          <div className="helper-text dpp-subtle" style={{ marginTop: '2px', marginBottom: '10px' }}>
            Posts this exact message automatically at the time you choose. Edits made after scheduling don’t affect a queued post.
          </div>
          <div className="dpp-schedule-row">
            <input type="datetime-local" className="input-field dpp-schedule-input" value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)} />
            <button type="button" className="btn-secondary" onClick={handleSchedule} disabled={scheduling || !messageText.trim()}>
              {scheduling ? 'Scheduling…' : 'Schedule Post'}
            </button>
          </div>
          {scheduleStatus && <div className="helper-text" style={{ marginTop: '8px' }}>{scheduleStatus}</div>}
        </div>

        {scheduledList.length > 0 && (
          <div className="dpp-queued">
            <div className="dpp-sub-label">Queued Posts</div>
            {scheduledList.map((it) => (
              <div key={it.id} className="dpp-queued-row">
                <span><strong>{fmtWhen(it.sendAt)}</strong>{it.overdue ? ' · sending shortly' : ''} — {it.preview}</span>
                <button type="button" className="dpp-link dpp-link-muted" onClick={() => handleCancelScheduled(it.id)}>Cancel</button>
              </div>
            ))}
          </div>
        )}
      </div>{/* /dpp-review */}
    </div>
  );
}

// ── Collapsed-summary helpers ───────────────────────────
function prepSummary(r) {
  const parts = [];
  parts.push(r.task && r.task.length ? r.task.join(' / ') : 'No task');
  if (has(r.pullLead)) parts.push(`Pull: ${r.pullLead.trim()}`);
  if (has(r.packLead)) parts.push(`Pack: ${r.packLead.trim()}`);
  if (has(r.teamMembers)) parts.push(`Crew: ${r.teamMembers.trim()}`);
  return parts.join(' · ');
}
function reshelveSummary(r) {
  const parts = [];
  if (has(r.lead)) parts.push(r.lead.trim());
  parts.push(has(r.teamMembers) ? `Crew: ${r.teamMembers.trim()}` : 'Crew missing');
  return parts.join(' · ');
}

// ── Monochrome line icons ───────────────────────────────
function Icon({ name }) {
  const p = {
    refresh: <><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" /><path d="M13.6 2.4V5H11" /></>,
    truck: <><rect x="1.5" y="4.5" width="8" height="6.5" rx="0.5" /><path d="M9.5 6.8h3l2 2.2v1.9h-5z" /><circle cx="4.3" cy="12.6" r="1.2" /><circle cx="11.4" cy="12.6" r="1.2" /></>,
    loadout: <><rect x="2.5" y="6.5" width="7" height="6" rx="0.5" /><path d="M12.5 2.5v5.4" /><path d="M10.3 5.7l2.2 2.2 2.2-2.2" /></>,
    pin: <><path d="M8 14s4.3-3.9 4.3-7.4A4.3 4.3 0 1 0 3.7 6.6C3.7 10.1 8 14 8 14z" /><circle cx="8" cy="6.4" r="1.5" /></>,
    box: <><path d="M8 2.2l5.5 3v6L8 14.2 2.5 11.2v-6z" /><path d="M2.6 5.3L8 8.2l5.4-2.9M8 8.2v6" /></>,
    returns: <><path d="M3.2 7.4A5 5 0 0 1 12 4.8" /><path d="M12.4 2.2v2.8H9.6" /><path d="M12.8 8.6A5 5 0 0 1 4 11.2" /><path d="M3.6 13.8V11H6.4" /></>,
    note: <><rect x="3.8" y="2.3" width="8.4" height="11.4" rx="0.6" /><path d="M6 5.5h4M6 8h4M6 10.5h2.6" /></>,
    chat: <path d="M2.5 4.2h11v6.6H7.2l-3 2.4v-2.4H2.5z" />,
    clock: <><circle cx="8" cy="8" r="5.6" /><path d="M8 4.8V8l2.2 1.5" /></>,
    check: <path d="M3.3 8.4l3 3 6.2-6.6" />,
    alert: <><path d="M8 2.4l6 10.6H2z" /><path d="M8 6.4v3.6M8 11.4v.3" /></>,
    chevronRight: <path d="M6 4l4 4-4 4" />,
    chevronDown: <path d="M4 6l4 4 4-4" />,
    dots: <><circle cx="8" cy="3" r="1.35" fill="currentColor" stroke="none" /><circle cx="8" cy="8" r="1.35" fill="currentColor" stroke="none" /><circle cx="8" cy="13" r="1.35" fill="currentColor" stroke="none" /></>
  }[name];
  return (
    <span className="dpp-ico" aria-hidden>
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
    </span>
  );
}

// Textarea that grows to fit its content (no manual drag needed). Measures
// after layout to avoid flicker and re-measures on every value change.
function AutoGrowTextarea({ value, minHeight = 64, className = '', style, onChange, ...rest }) {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  }, [minHeight]);
  useLayoutEffect(() => { resize(); }, [value, resize]);
  return (
    <textarea ref={ref} className={className} style={{ minHeight, overflowY: 'hidden', resize: 'none', ...style }}
      value={value} onChange={onChange} onInput={resize} {...rest} />
  );
}

function CategoryHeader({ icon, title, count }) {
  return (
    <div className="dpp-cat">
      <Icon name={icon} />
      <span className="dpp-cat-title">{title}</span>
      <span className="dpp-cat-count">· {count}</span>
    </div>
  );
}

function EmptyCategory({ label, addLabel, onAdd }) {
  return (
    <div className="dpp-empty">
      <span className="dpp-empty-text">{label}</span>
      <button type="button" className="dpp-link" onClick={onAdd}>{addLabel}</button>
    </div>
  );
}

function StatusTag({ status }) {
  if (status.complete) return <span className="dpp-status dpp-status-ok"><Icon name="check" />Complete</span>;
  const n = status.missing.length;
  return <span className="dpp-status dpp-status-warn"><Icon name="alert" />{n} detail{n === 1 ? '' : 's'} needed</span>;
}

function SubLabel({ children }) {
  return <div className="dpp-sub-label">{children}</div>;
}

function AddLink({ label, onClick }) {
  return <button type="button" className="dpp-addlink" onClick={onClick}>{label}</button>;
}

function Picker({ options, value, onChange, loading }) {
  return (
    <SearchableSelect options={options} value={value} onChange={onChange}
      placeholder={loading ? 'Loading…' : 'Search or select a project…'} loading={loading} />
  );
}

function EField({ label, value, onChange, placeholder = '', half, required, optional, labelHidden }) {
  const missing = required && !has(value);
  return (
    <div className={`dpp-field${half ? ' form-section-half' : ''}${missing ? ' dpp-missing' : ''}`}>
      {!labelHidden && (
        <label className="form-label">
          {label}{optional && <span className="dpp-optional">Optional</span>}
        </label>
      )}
      <input type="text" className="input-field" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function RefField({ label, value }) {
  return (
    <div className="dpp-ref">
      <div className="dpp-ref-label">{label}</div>
      <div className="dpp-ref-value">{value}</div>
    </div>
  );
}

// Event with Warehouse-Manager assignment fields + completion status.
function OperationEvent({
  kind, noun, r, idx, options, timingKey, timingLabel, projectLabel = 'Project',
  showPacket, onSelect, onUpdate, onRemove, collapsed, onToggleCollapse, loading, refFor
}) {
  const key = `${kind}-${idx}`;
  const selected = has(r.project) || !!r.selectKey || r.manual;
  const st = reqStatus(r);
  const canCollapse = has(r.project);
  const isCollapsed = canCollapse && collapsed.has(key);
  const timing = r[timingKey];
  const timingImported = !r.manual && !!r.selectKey && has(timing);
  const timingShort = (timingLabel || '').replace(/ Time$/, '');
  const metaParts = [];
  if (timingImported) metaParts.push({ label: timingShort, value: timing });
  if (!r.manual && has(r.rwTicket)) metaParts.push({ label: 'RW Ticket', value: r.rwTicket });
  return (
    <div className="dpp-event" ref={(el) => refFor(key, el)}>
      <div className="dpp-event-head">
        <button type="button" className="dpp-collapse" disabled={!canCollapse}
          onClick={() => onToggleCollapse(key)} aria-label={isCollapsed ? 'Expand' : 'Collapse'}>
          <Icon name={isCollapsed ? 'chevronRight' : 'chevronDown'} />
        </button>
        <div className="dpp-event-titlewrap">
          <div className="dpp-event-title">{r.project || 'New operation'}</div>
          {isCollapsed
            ? <div className="dpp-event-summary">{opSummary(noun, r, timing)}</div>
            : <div className="dpp-event-sub">{noun}{r.rwTicket ? ` · RW #${r.rwTicket}` : ''} · {r.manual ? 'Added manually' : (r.selectKey ? 'Auto-pulled' : 'Not linked')}</div>}
        </div>
        {canCollapse && <StatusTag status={st} />}
      </div>

      {!isCollapsed && (
        <div className="dpp-event-body">
          <SubLabel>Project</SubLabel>
          <Picker options={options} value={r.selectKey} onChange={(v) => onSelect(idx, v)} loading={loading} />
          {r.manual && <EField label={projectLabel} value={r.project} onChange={(v) => onUpdate(idx, { project: v })} />}

          {selected && (
            <>
              <SubLabel>Assignment</SubLabel>
              <div className="form-row">
                <EField half required label="Team Lead" value={r.lead} onChange={(v) => onUpdate(idx, { lead: v })} />
                <EField half required label="Crew" placeholder="e.g. Tye, Dre, Jalan" value={r.teamMembers} onChange={(v) => onUpdate(idx, { teamMembers: v })} />
              </div>
              <div className="form-row">
                <EField half required label="Assigned Vehicle" placeholder="e.g. Box Truck 1" value={r.vehicle} onChange={(v) => onUpdate(idx, { vehicle: v })} />
                <EField half required label="Departure Location" value={r.departure} onChange={(v) => onUpdate(idx, { departure: v })} />
              </div>

              {(!timingImported || r.manual) && (
                <div className="form-row">
                  {!timingImported && (
                    <EField half optional label={timingLabel} value={timing} onChange={(v) => onUpdate(idx, { [timingKey]: v })} />
                  )}
                  {r.manual && (
                    <EField half optional label="RW Ticket" value={r.rwTicket} onChange={(v) => onUpdate(idx, { rwTicket: v })} />
                  )}
                </div>
              )}

              {metaParts.length > 0 && (
                <div className="dpp-meta">
                  {metaParts.map((p, i) => (
                    <span key={i} className="dpp-meta-item"><span className="dpp-meta-label">{p.label}:</span> {p.value}</span>
                  ))}
                </div>
              )}

              {showPacket && (
                <EField optional label="Production Packet Link" placeholder="Auto-resolved from the project folder; edit to override"
                  value={r.packet} onChange={(v) => onUpdate(idx, { packet: v })} />
              )}

              <SubLabel>Notes</SubLabel>
              <EField labelHidden optional label="Notes" placeholder="Add any special instructions for this operation…"
                value={r.notes} onChange={(v) => onUpdate(idx, { notes: v })} />

              <div className="dpp-event-foot">
                <button type="button" className="dpp-remove" onClick={() => onRemove(idx)}>Remove Operation</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Event for categories without required-field tracking (prep, returns).
function PlainEvent({ kind, r, idx, noun, summary, collapsed, onToggleCollapse, refFor, onRemove, children }) {
  const key = `${kind}-${idx}`;
  const canCollapse = has(r.project);
  const isCollapsed = canCollapse && collapsed.has(key);
  return (
    <div className="dpp-event" ref={(el) => refFor(key, el)}>
      <div className="dpp-event-head">
        <button type="button" className="dpp-collapse" disabled={!canCollapse}
          onClick={() => onToggleCollapse(key)} aria-label={isCollapsed ? 'Expand' : 'Collapse'}>
          <Icon name={isCollapsed ? 'chevronRight' : 'chevronDown'} />
        </button>
        <div className="dpp-event-titlewrap">
          <div className="dpp-event-title">{r.project || 'New operation'}</div>
          {isCollapsed
            ? <div className="dpp-event-summary">{summary}</div>
            : <div className="dpp-event-sub">{noun}{r.rwTicket ? ` · RW #${r.rwTicket}` : ''} · {r.manual ? 'Added manually' : (r.selectKey ? 'Auto-pulled' : 'Not linked')}</div>}
        </div>
      </div>
      {!isCollapsed && (
        <div className="dpp-event-body">
          {children}
          <div className="dpp-event-foot">
            <button type="button" className="dpp-remove" onClick={onRemove}>Remove Operation</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductionSchedule;
